import React, {ReactNode} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FinderUtil from 'file-browser/FinderUtil';
// @ts-expect-error
import FilesForDeleteModal from 'folder-items/FilesForDeleteModal';
import {getStore} from 'redux-stores/Store';
import {ListableItem} from 'search/SearchConstants';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

interface Props {
  className?: string;
  disabled: boolean;
  files: Array<ListableItem>;
  finderId: string;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

function getDeleteDescription(items: Array<ListableItem>): ReactNode {
  const isFolder = (item: ListableItem): boolean =>
    APIUtil.apiIdToType(item.id) === ModelTypeConstants.FOLDER;
  if (items.length === 1) {
    const item = items[0];
    const itemTypeName = isProject(item) ? 'project' : isFolder(item) ? 'folder' : 'item';
    return (
      <span>
        the {itemTypeName} <strong>{item.name}</strong>
      </span>
    );
  } else {
    return items.every((item) => isFolder(item)) ? 'these folders' : 'these items';
  }
}

function isProject(item: ListableItem): boolean {
  const type = APIUtil.apiIdToType(item.id);

  if (type !== ModelTypeConstants.FOLDER) {
    return false;
  }

  const folder = item as StringIDFolder;
  return folder.parent == null && folder.source?.sourceType === ModelTypeConstants.PROJECT;
}

function getTooltip(isNotArchived: boolean, hasMultipleItemsSelectedWithProject: boolean): string {
  if (hasMultipleItemsSelectedWithProject) {
    return 'Projects must be deleted individually';
  }
  if (isNotArchived) {
    return 'Only archived items can be deleted';
  }
  return 'Delete';
}

function DeleteButton({className, files, finderId, onClickTracking, disabled = false}: Props): JSX.Element {
  const hasMultipleItemsSelectedWithProject = files.length > 1 && files.some((file) => isProject(file));
  const tooltipTitle = getTooltip(
    disabled && BenchlingConfig.ENABLE_ARCHIVING_V2,
    hasMultipleItemsSelectedWithProject
  );

  function handleOpenDeleteConfirm(): void {
    onClickTracking?.();
    if (files.length === 1 && isProject(files[0])) {
      const source = FinderUtil.sourceToRootFolder((files[0] as StringIDFolder).source!).source!;
      const fileId = source.rootFolder.api_identifier;
      FilesForDeleteModal.open({
        fileIds: [fileId],
        doDelete: () => {
          FileBrowserActions.deleteSource(source.id).done(() => {
            const section = NavConstants.FINDER_ID_TO_SECTION[finderId];
            getStore().dispatch(SearchUIReduxActions.refreshListing(section));
          });
        },
        deleteDescription: (
          <span>
            the project <strong>{source.name}</strong>
          </span>
        ),
      });
      return;
    }

    const deleteDescription = getDeleteDescription(files);
    FilesForDeleteModal.openForFileDelete({deleteDescription}, files, finderId);
  }

  const isDisabled = disabled || hasMultipleItemsSelectedWithProject;

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        aria-label="Delete"
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        className={className}
        disabled={isDisabled}
        icon="delete"
        onClick={handleOpenDeleteConfirm}
        size={ButtonSize.SMALL}
        dataTestAttrs={{component: 'DeleteButton', element: 'root'}}
      />
    </Tooltip>
  );
}

export default DeleteButton;
